import { useCallback, useEffect, type Dispatch } from 'react';

import type { AgentRunInputState, RunTransport } from '../../contracts';
import {
  isTurnCancellable,
  type TurnLifecycleAction,
  type TurnPhase,
} from '../harness/turn-lifecycle';
import { useMutateCancelTurn } from '../mutation/useMutateCancelTurn';

const CANCEL_SETTLE_TIMEOUT_MS = 45_000;

export interface UseRunCancellationParams {
  agentRunId: string | undefined;
  transport: RunTransport;
  phase: TurnPhase;
  inputState: AgentRunInputState | undefined;
  refetch: () => Promise<unknown>;
  refetchAndDetectDiscard: () => void;
  dispatchTurn: Dispatch<TurnLifecycleAction>;
  /** The lifecycle options object, held whole — see the dependency note below. */
  options: { onCancelFailed?: () => void };
}

export const useRunCancellation = ({
  agentRunId,
  transport,
  phase,
  inputState,
  refetch,
  refetchAndDetectDiscard,
  dispatchTurn,
  options,
}: UseRunCancellationParams): (() => void) => {
  const { mutate: cancelMutate, isPending: cancelIsPending } =
    useMutateCancelTurn(transport);

  const canCancel = isTurnCancellable(phase, inputState);

  const handleCancelTurn = useCallback(() => {
    if (!agentRunId || phase === 'cancelling' || cancelIsPending) return;
    if (!canCancel) {
      // The turn already ended (`ended`) or none is running — the snapshot may
      // still read 'turn_running' for a poll cycle, but POSTing a cancel here
      // would hit a finished turn and the backend would reject it (the "cancel
      // too many turns -> backend error" report). Reconcile instead.
      void refetch();
      return;
    }
    cancelMutate(agentRunId, {
      onSuccess: () => dispatchTurn({ type: 'CANCEL_REQUESTED' }),
      onError: () => {
        // Lost a race to the turn finishing on its own (or a transient BE
        // error): refetch so the composer reconciles to the real run state
        // instead of stranding "Stopping…".
        void refetch();
        options.onCancelFailed?.();
      },
    });
    // `options` is the dependency rather than `options.onCancelFailed`: the
    // pre-carve list did not name the toast either, the callback is read through
    // the object at fire time, and narrowing it here would change when this
    // handler is rebuilt.
  }, [
    agentRunId,
    canCancel,
    cancelIsPending,
    cancelMutate,
    dispatchTurn,
    options,
    phase,
    refetch,
  ]);

  useEffect(() => {
    if (phase !== 'cancelling') return;
    const timeout = window.setTimeout(() => {
      // Same detection as the SSE boundary: if the cancel landed on the backend
      // (snapshot tail [user, marker]) but we never saw the boundary, still
      // recover the prompt rather than silently suppressing it.
      refetchAndDetectDiscard();
      dispatchTurn({ type: 'CANCEL_TIMEOUT' });
    }, CANCEL_SETTLE_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [dispatchTurn, phase, refetchAndDetectDiscard]);

  return handleCancelTurn;
};
